import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, useQueryFetch } from 'sonata-react-components';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { DO_NOT_DISTURB_TARGETING_SECTION_KEY } from '../common/common';
import { SelectionableSectionWithConfirmation } from '../common/SelectionableSectionWithConfirmation';
import EmptyState from './EmptyState/EmptyState';
import { getLQITags } from './helpers/getSectionHeaderTags.helper';
import { getLQIIndexes } from './LQI.resources';
import LQlIndexSelector from './LQlIndexSelector/LQlIndexSelector';
import SelectedIndexLQI from './SelectedIndexLQI/SelectedIndexLQI';

import './lQIQualityAssurance.styles.scss';

const DEFAULT_DISTRIBUTION = 0;
const DEFAULT_THRESHOLD = 16.5;

type State = {
  selectedIndex?: Record<string, any>;
  showIndexSelector: boolean;
};

function LQIQualityAssurance() {
  const { formatMessage } = useIntl();
  const { setProp, targeting, resetSection } = useTargetingContext();
  const { lqi } = targeting;

  const isPreBidEnabled = Boolean(lqi?.prebid?.enabled);
  const isPostBidEnabled = Boolean(lqi?.postbid?.enabled);

  const [{ showIndexSelector }, setState] = useState<State>({
    showIndexSelector: false
  });

  const handleChangeSelectedIndex = (index?: Record<string, any>) =>
    setState((prev: State) => ({
      ...prev,
      selectedIndex: index
    }));

  const { data, isLoading } = useQueryFetch<Record<string, any>[]>(['LQI_indexes'], async () => {
    const indexes = await getLQIIndexes();
    const defaultSelected = indexes.find(({ id }) => id === lqi?.prebid?.index);
    if (defaultSelected) {
      handleChangeSelectedIndex(defaultSelected);
    }
    return indexes;
  });

  const selectedIndex = data?.find(({ id }) => id === lqi?.prebid?.index);

  const resetLQIFields = () => {
    resetSection('lqiQualityAssurance');
  };

  const handleSetIndex = (index: Record<string, any>) => {
    setProp('lqi', {
      postbid: {
        enabled: true
      },
      prebid: {
        distribution: DEFAULT_DISTRIBUTION,
        threshold: DEFAULT_THRESHOLD,
        ...lqi.prebid,
        enabled: true,
        index: index.id
      }
    });
    handleChangeSelectedIndex(index);
  };

  const handlePrebidSectionChange = (enabled: boolean) => {
    if (!enabled) {
      setProp('lqi', {
        postbid: false
      });
    }
    const isEnablingPreBidUniqueOption = enabled && data?.length === 1;
    if (isEnablingPreBidUniqueOption) {
      handleSetIndex(data[0]);
    }
  };

  const handleSetPostBid = (enabled: boolean) => {
    setProp('lqi', {
      ...lqi,
      postbid: {
        enabled
      }
    });
  };

  const handleToggleIndexSelector = (newState: boolean) => () =>
    setState((prev: State) => ({
      ...prev,
      showIndexSelector: newState
    }));

  const handleResetLQI = () => {
    resetSection('lqiQualityAssurance');
  };

  const isSectionOpened = isPreBidEnabled || isPostBidEnabled;
  const lqiTags = getLQITags(lqi);

  return (
    <div className="lqiQualityAssurenceSection" id="lqiQualityAssuranceBox">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetLQI}
        preffixId="lqiQualityAssurance"
        tags={lqiTags}
        title={formatMessage({
          defaultMessage: 'Location Quality Indexes',
          id: 'targeting.lqi.title'
        })}
        tooltipText={formatMessage({
          defaultMessage: "Filter incoming ad requests based on the precision and accuracy of the user's GPS data.",
          id: 'targeting.lqi.tooltip'
        })}
      >
        {!isLoading && (
          <div className="lqiQualityAssurenceSection__boxBody">
            <SelectionableSectionWithConfirmation
              id="lqi-prebid-section"
              isSectionEnabled={isPreBidEnabled}
              label={formatMessage({
                defaultMessage: 'LQI index - PRE BID',
                id: 'targeting.lqi.preBid.label'
              })}
              modalConfirm={{
                storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
              }}
              onSectionEnabledChange={handlePrebidSectionChange}
            >
              {selectedIndex ? (
                <SelectedIndexLQI
                  onEdit={handleToggleIndexSelector(true)}
                  onRemove={resetLQIFields}
                  selectedIndex={selectedIndex}
                />
              ) : (
                <EmptyState onClick={handleToggleIndexSelector(true)} />
              )}
            </SelectionableSectionWithConfirmation>
            <div className="lqiQualityAssurenceSection__postBid">
              <Checkbox
                active={isPostBidEnabled}
                classes="lqiQualityAssurenceSection__postBidCheckbox"
                defaultActive={isPostBidEnabled}
                disabled={isPreBidEnabled}
                id="lqi-postbid-section"
                labelText={formatMessage({
                  defaultMessage: 'LQI index - POST BID',
                  id: 'targeting.lqi.postBid.label'
                })}
                onFilter={handleSetPostBid}
              />
            </div>
          </div>
        )}
      </FormExpandableSection>
      {showIndexSelector && data && (
        <LQlIndexSelector indexes={data} onClose={handleToggleIndexSelector(false)} onSelect={handleSetIndex} />
      )}
    </div>
  );
}

export default LQIQualityAssurance;
